import React, { useState, useMemo, useRef, useEffect } from 'react'
import { StudentDetail } from '../../instructorDashboard/components/StudentDetail'
import ReplayWorkspace from '../../workspaceReplay2/ReplayWorkspace'
import { usePlayback } from '../../workspaceReplay2/hooks/usePlayback'
import useIsMobile from './useIsMobile'

const COURSE_DISPLAY = {
  Python: { name: 'Intro to Programming in Python', code: 'CS 1064' },
  C:      { name: 'Introduction to Programming in C', code: 'CS 1044' },
}

// ── Workspace Frame ───────────────────────────────────────────────────────────

function WorkspaceFrame({ events, startTime, duration, task = '', starterCode = '', testCases = [] }) {
  const playback = usePlayback(duration)
  const seekedRef = useRef(false)

  useEffect(() => {
    if (!seekedRef.current && duration > 0) {
      seekedRef.current = true
      const t = setTimeout(() => playback.seek(duration * 0.9), 100)
      return () => clearTimeout(t)
    }
  }, [duration])

  return (
    <div style={{
      position: 'absolute',
      top: 0, left: 0,
      width: '1200px',
      height: '700px',
      transform: 'scale(0.30)',
      transformOrigin: 'top left',
      pointerEvents: 'none',
    }}>
      <ReplayWorkspace
        playback={playback}
        events={events}
        startTime={startTime}
        task={task}
        starterCode={starterCode}
        testCases={testCases}
      />
    </div>
  )
}

// ── Lazy Thumbnail ────────────────────────────────────────────────────────────

function LazyWorkspaceThumbnail({ events, startTime, duration, segments, totalDuration, task, starterCode, testCases }) {
  const containerRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const hasEvents = events && events.length > 0 && duration > 0

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: '100px 0px', threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{
      position: 'relative',
      width: '100%',
      height: '160px',
      minHeight: '160px',
      overflow: 'hidden',
      backgroundColor: '#1e1e2e',
      borderRadius: '10px 10px 0 0',
    }}>
      {isVisible && hasEvents ? (
        <>
          <WorkspaceFrame events={events} startTime={startTime} duration={duration} task={task} starterCode={starterCode} testCases={testCases} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }} />
        </>
      ) : (
        <div style={{ display: 'flex', width: '100%', height: '100%', padding: '12px' }}>
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '6px', borderRight: '1px solid #2a2a3a', paddingRight: '12px' }}>
            {[65, 80, 45, 70, 55, 90, 40, 75, 60, 50].map((w, i) => (
              <div key={i} style={{ height: '4px', borderRadius: '2px', backgroundColor: '#333', width: `${w}%` }} />
            ))}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '12px', justifyContent: 'flex-end' }}>
            {[70, 55, 80, 60].map((w, i) => (
              <div key={i} style={{
                height: '12px', borderRadius: '4px', backgroundColor: '#2a2a3a',
                width: `${w}%`, alignSelf: i % 2 === 0 ? 'flex-end' : 'flex-start',
              }} />
            ))}
          </div>
        </div>
      )}

      {segments && segments.length > 0 && totalDuration > 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '8px', display: 'flex', zIndex: 11, boxShadow: '0 -2px 6px rgba(0,0,0,0.3)' }}>
          {segments.map((seg, i) => {
            const dur = seg.endTime - seg.startTime
            const pct = (dur / totalDuration) * 100
            if (pct < 0.5) return null
            return <div key={i} style={{ width: `${pct}%`, backgroundColor: seg.behavior?.color || '#6b7280', minWidth: '1px' }} />
          })}
        </div>
      )}

      <div className="play-overlay" style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 12, pointerEvents: 'none',
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          paddingLeft: '2px',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><polygon points="8,5 20,12 8,19"/></svg>
        </div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SessionList({ deployment, data, onLoadDeployment, caseStudyData }) {
  const isMobile = useIsMobile()
  const [searchQuery, setSearchQuery] = useState('')
  const [resultFilter, setResultFilter] = useState('all')
  const [chatFilter, setChatFilter] = useState('all')
  const [sortBy, setSortBy] = useState('default')
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [detailTab, setDetailTab] = useState('replay')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 9

  const { students = [], analyticsById = {}, loading, error } = data || {}

  useEffect(() => {
    if (!selectedStudentId) return
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        setSelectedStudentId(null)
        setDetailTab('replay')
      }
    }
    window.addEventListener('keydown', handleEsc, true)
    return () => window.removeEventListener('keydown', handleEsc, true)
  }, [selectedStudentId])

  const parsedSession = useMemo(() => {
    if (!data?.session) return { task: '', starterCode: '', testCases: [] }
    let starterCode = ''
    try { starterCode = (typeof data.session.test_code === 'string' ? JSON.parse(data.session.test_code) : (data.session.test_code || {})).template || '' } catch {}
    let testCases = []
    try {
      if (data.session.test_list) {
        const raw = data.session.test_list
        const items = typeof raw === 'string'
          ? raw.replace(/^\{/, '').replace(/\}$/, '').split(/(?<=\})\s*,\s*(?=\{|")/)
          : Array.isArray(raw) ? raw : []
        testCases = items.map(item => {
          try {
            const cleaned = typeof item === 'string' ? item.replace(/^"|"$/g, '').replace(/\\"/g, '"') : item
            return typeof cleaned === 'string' ? JSON.parse(cleaned) : cleaned
          } catch { return null }
        }).filter(Boolean)
      }
    } catch {}
    return { task: data.session.task || '', starterCode, testCases }
  }, [data?.session])

  const filteredStudents = useMemo(() => {
    let result = [...students]
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(s =>
        (s.display_name?.toLowerCase().includes(q)) ||
        (s.username?.toLowerCase().includes(q)) ||
        (s.first_name?.toLowerCase().includes(q)) ||
        (s.last_name?.toLowerCase().includes(q)) ||
        (String(s.id).includes(q))
      )
    }
    if (resultFilter === 'passed') result = result.filter(s => s.tests_passed === s.tests_total && s.tests_total > 0)
    else if (resultFilter === 'failed') result = result.filter(s => s.tests_passed < s.tests_total || !s.tests_total)
    if (chatFilter === 'used') result = result.filter(s => s.chat_count > 0)
    else if (chatFilter === 'none') result = result.filter(s => !s.chat_count || s.chat_count === 0)

    // Deterministic pseudo-random hash for stable "shuffled" default order
    const hashId = (id) => {
      let h = 0
      const s = String(id)
      for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
      return h
    }
    result.sort((a, b) => {
      let cmp = 0
      if (sortBy === 'duration') cmp = (b.duration || 0) - (a.duration || 0)
      else if (sortBy === 'queries') cmp = (b.chat_count || 0) - (a.chat_count || 0)
      else if (sortBy === 'tests') cmp = (b.tests_passed || 0) - (a.tests_passed || 0)
      else cmp = hashId(a.id) - hashId(b.id)
      return cmp !== 0 ? cmp : String(a.id).localeCompare(String(b.id))
    })
    return result
  }, [students, searchQuery, resultFilter, chatFilter, sortBy, analyticsById])

  useEffect(() => { setPage(0) }, [searchQuery, resultFilter, chatFilter, sortBy])

  const totalPages = Math.ceil(filteredStudents.length / PAGE_SIZE)
  const pagedStudents = filteredStudents.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const getName = (s) =>
    s.display_name || s.username ||
    `${s.first_name || ''} ${s.last_name || ''}`.trim() ||
    `Student ${String(s.id).slice(-4)}`

  const fmtDur = (sec) => {
    if (!sec) return '\u2014'
    const m = Math.floor(sec / 60), s = sec % 60
    return `${m}m ${s}s`
  }

  const buildStudentDetailData = (student, rawEvents) => {
    const name = getName(student)
    if (!rawEvents || rawEvents.length === 0) {
      return {
        id: student.id, name, hasLlmMessage: false, llmMessageCount: 0, passedAllTests: false,
        submissionTime: new Date().toISOString(), testsPassed: 0, duration: 0,
        conversation: [], replayEvents: [], behavior: {},
        aiUsage: { totalQueries: 0, avgQueryLengthWords: 0, timeToFirstQuery: 0, chatToCodeLatency: 0, errorToQueryLatency: 0, iDontKnowRatio: 0, specificQuestionRatio: 0 },
        timeToFirstRun: 0, timeToCompletion: 0, behavioralSummary: 'No telemetry data available.',
      }
    }
    const startTime = rawEvents[0].timestamp
    const lastEvent = rawEvents[rawEvents.length - 1]
    const duration = Math.round((lastEvent.timestamp - startTime) / 1000)
    const queries = rawEvents.filter(e => e.type === 'CHAT_QUERY')
    const chatEvents = rawEvents.filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
    const conversation = chatEvents.map(e => ({
      role: e.type === 'CHAT_QUERY' ? 'user' : 'assistant',
      content: e.payload?.text || e.payload?.message || e.payload?.content || '',
      timestamp: e.timestamp,
    })).sort((a, b) => a.timestamp - b.timestamp)
    const testResults = rawEvents.filter(e => e.type === 'TEST_CASE_RESULT')
    const lastTest = testResults[testResults.length - 1]
    const testsPassed = lastTest?.payload?.passed_count || 0
    const testsTotal = lastTest?.payload?.total_tests || 0
    const passedAll = testsTotal > 0 && testsPassed === testsTotal
    const firstQuery = queries[0]
    const timeToFirstQuery = firstQuery ? Math.round((firstQuery.timestamp - startTime) / 1000) : 0
    const firstRun = rawEvents.find(e => e.type === 'TERMINAL_RUN')
    const timeToFirstRun = firstRun ? Math.round((firstRun.timestamp - startTime) / 1000) : 0
    const errors = rawEvents.filter(e => e.type === 'TERMINAL_ERROR')
    const summaryParts = [`Completed in ${Math.round(duration / 60)}m.`]
    summaryParts.push(passedAll ? `Passed all ${testsTotal} tests.` : `Passed ${testsPassed}/${testsTotal} tests.`)
    summaryParts.push(queries.length > 0 ? `Used AI ${queries.length} time${queries.length !== 1 ? 's' : ''}, first at ${Math.round(timeToFirstQuery / 60)}m.` : 'Did not use AI assistance.')
    if (errors.length > 0) summaryParts.push(`Encountered ${errors.length} error${errors.length !== 1 ? 's' : ''}.`)
    return {
      id: student.id, name, hasLlmMessage: queries.length > 0, llmMessageCount: queries.length, passedAllTests: passedAll,
      submissionTime: new Date(lastEvent.timestamp).toISOString(), testsPassed, duration, conversation, replayEvents: [],
      behavior: {},
      aiUsage: { totalQueries: queries.length, avgQueryLengthWords: 0, timeToFirstQuery, chatToCodeLatency: 0, errorToQueryLatency: 0, iDontKnowRatio: 0, specificQuestionRatio: 0 },
      timeToFirstRun, timeToCompletion: duration, behavioralSummary: summaryParts.join(' '),
    }
  }

  if (loading) {
    return (
      <div style={styles.centeredState}>
        <div style={styles.spinner} />
        <span style={{ fontSize: '14px', color: '#888' }}>Loading telemetry for {deployment.course} ({deployment.date})... This only happens once — data will be cached for future visits.</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }
  if (error) {
    return (
      <div style={styles.centeredState}>
        <span style={{ fontSize: '14px', color: '#ef4444' }}>Error: {error}</span>
        <button onClick={onLoadDeployment} style={styles.retryButton}>Retry</button>
      </div>
    )
  }
  if (students.length === 0) {
    return (
      <div style={styles.centeredState}>
        <span style={{ fontSize: '14px', color: '#999' }}>No students found for this deployment.</span>
      </div>
    )
  }

  const Pill = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{
      padding: '6px 16px', borderRadius: '20px',
      border: active ? '1.5px solid #111' : '1.5px solid #e0e0e3',
      background: active ? '#111' : '#fff',
      color: active ? '#fff' : '#555',
      fontSize: '13px', fontWeight: 500, cursor: 'pointer',
      transition: 'all 0.15s', whiteSpace: 'nowrap',
    }}>{label}</button>
  )

  return (
    <div style={{ ...styles.container, ...(isMobile ? { padding: '0 16px 16px' } : {}) }}>
      <style>{`
        .student-card:hover .play-overlay { opacity: 1 !important; }
        .student-card .play-overlay { opacity: 0; transition: opacity 0.2s; }
      `}</style>

      <div style={styles.header}>
        <div style={{ flex: 1 }}>
          <h2 style={styles.title}>{(COURSE_DISPLAY[deployment.language] || {}).name || deployment.course}</h2>
          <p style={styles.subtitle}>{deployment.label} · {(COURSE_DISPLAY[deployment.language] || {}).code || deployment.course} · {deployment.date} · {deployment.time}</p>
        </div>
      </div>

      <div style={styles.searchRow}>
        <div style={styles.searchBox}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Search students..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={styles.searchInput} />
        </div>
        <div style={styles.sortSelect}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="16" y2="6"/><line x1="4" y1="12" x2="12" y2="12"/><line x1="4" y1="18" x2="8" y2="18"/></svg>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={styles.sortDropdown}>
            <option value="default">Default</option>
            <option value="duration">Duration</option>
            <option value="queries">AI Queries</option>
            <option value="tests">Tests Passed</option>
          </select>
        </div>
      </div>

      <div style={styles.pillRow}>
        <Pill label="All" active={resultFilter === 'all' && chatFilter === 'all'} onClick={() => { setResultFilter('all'); setChatFilter('all') }} />
        <Pill label="Passed" active={resultFilter === 'passed'} onClick={() => setResultFilter(resultFilter === 'passed' ? 'all' : 'passed')} />
        <Pill label="Has Failures" active={resultFilter === 'failed'} onClick={() => setResultFilter(resultFilter === 'failed' ? 'all' : 'failed')} />
        <div style={styles.pillDivider} />
        <Pill label="Used AI" active={chatFilter === 'used'} onClick={() => setChatFilter(chatFilter === 'used' ? 'all' : 'used')} />
        <Pill label="No AI" active={chatFilter === 'none'} onClick={() => setChatFilter(chatFilter === 'none' ? 'all' : 'none')} />
        <span style={styles.filterCount}>{filteredStudents.length} of {students.length}</span>
      </div>

      <div style={{ ...styles.cardGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
        {pagedStudents.map(student => {
          const a = analyticsById[student.id]?.analytics || analyticsById[String(student.id)]?.analytics
          const entry = analyticsById[student.id] || analyticsById[String(student.id)]
          const rawEvents = entry?.rawEvents || []
          const stats = a?.sessionStats?.full
          const runs = stats?.frequency?.terminalRun?.count ?? 0
          const errors = stats?.frequency?.terminalError?.count ?? 0
          const segments = a?.autoSegments?.filter(s => !s.isSystemEvent) || []
          const totalDuration = a?.replayDuration || 0
          const replayStartTime = a?.replayStartTime || (rawEvents[0]?.timestamp ?? 0)
          const username = student.username || student.display_name || `ID ${String(student.id).slice(-6)}`
          const sessionDate = rawEvents[0]?.timestamp ? new Date(rawEvents[0].timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null

          return (
            <div key={student.id} className="student-card" onClick={() => setSelectedStudentId(student.id)}
              style={{ ...styles.card, ...(isMobile ? { maxWidth: 'none' } : {}) }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#c0c0c5'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8eb'; e.currentTarget.style.boxShadow = 'none' }}
            >
              <LazyWorkspaceThumbnail
                events={a?.filteredEvents || rawEvents}
                startTime={replayStartTime}
                duration={totalDuration}
                segments={segments}
                totalDuration={totalDuration}
                task={parsedSession.task}
                starterCode={parsedSession.starterCode}
                testCases={parsedSession.testCases}
              />

              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '12px 14px 14px',
                backgroundColor: '#fff',
                position: 'relative',
                zIndex: 5,
                borderTop: '1px solid #f0f0f2',
                minHeight: '72px',
              }}>
                <div style={styles.cardAvatarSmall}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={styles.cardName}>{getName(student)}</div>
                  </div>
                  <div style={styles.cardUsername}>{username}{sessionDate ? ` · ${sessionDate}` : ''}</div>

                  <div style={styles.cardChips}>
                    <span style={styles.chip}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {fmtDur(student.duration)}
                    </span>
                    <span style={styles.chip}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      {student.chat_count || 0} queries
                    </span>
                    <span style={styles.chip}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>
                      {runs} runs
                    </span>
                    <span style={styles.chip}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                      {errors} errors
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ ...styles.pageBtn, opacity: page === 0 ? 0.3 : 1, cursor: page === 0 ? 'not-allowed' : 'pointer' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Previous
          </button>

          <div style={styles.pageNumbers}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                style={{
                  ...styles.pageNum,
                  backgroundColor: page === i ? '#111' : 'transparent',
                  color: page === i ? '#fff' : '#666',
                  fontWeight: page === i ? 600 : 400,
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            style={{ ...styles.pageBtn, opacity: page === totalPages - 1 ? 0.3 : 1, cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer' }}
          >
            Next
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}

      {/* ── Student Detail Modal ─────────────────────────────────────── */}
      {selectedStudentId && (() => {
        const student = students.find(s => s.id === selectedStudentId || String(s.id) === String(selectedStudentId))
        if (!student) return null
        const entry = analyticsById[student.id] || analyticsById[String(student.id)]
        const rawEvents = entry?.rawEvents || []
        const precomputed = entry?.analytics || null
        const studentData = buildStudentDetailData(student, rawEvents)

        return (
          <div style={{ ...styles.modalOverlay, ...(isMobile ? { padding: 0 } : {}) }}>
            <div style={{
              background: 'white', borderRadius: isMobile ? '0' : '14px',
              position: 'relative',
              maxWidth: isMobile ? '100%' : (detailTab === 'replay' ? '1400px' : '900px'),
              width: isMobile ? '100%' : (detailTab === 'replay' ? '95%' : '90%'),
              height: isMobile ? '100dvh' : '90vh', maxHeight: isMobile ? '100dvh' : '90vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              transition: 'max-width 0.2s ease',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
            }}>
              {/* Download button */}
              <button
                onClick={() => {
                  const blob = new Blob([JSON.stringify({
                    student_id: student.id,
                    name: getName(student),
                    session_id: deployment.sessionId,
                    deployment: deployment.id,
                    event_count: rawEvents.length,
                    events: rawEvents,
                  }, null, 2)], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `telemetry_${student.id}_${deployment.id}.json`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
                title={`Download ${rawEvents.length.toLocaleString()} events as JSON`}
                style={{
                  position: 'absolute', top: '12px', right: '12px', zIndex: 10,
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '6px 12px', borderRadius: '8px',
                  border: '1.5px solid #e0e0e3', background: '#fff',
                  fontSize: '12px', fontWeight: 500, color: '#555',
                  cursor: 'pointer', transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>

              <StudentDetail
                student={studentData}
                rawEvents={rawEvents}
                precomputedAnalytics={precomputed}
                sessionData={(() => {
                  if (!data?.session) return null
                  let starterCode = ''
                  try { starterCode = (typeof data.session.test_code === 'string' ? JSON.parse(data.session.test_code) : (data.session.test_code || {})).template || '' } catch {}
                  let testCases = []
                  try {
                    if (data.session.test_list) {
                      const raw = data.session.test_list
                      const items = typeof raw === 'string'
                        ? raw.replace(/^\{/, '').replace(/\}$/, '').split(/(?<=\})\s*,\s*(?=\{|")/)
                        : Array.isArray(raw) ? raw : []
                      testCases = items.map(item => {
                        try {
                          const cleaned = typeof item === 'string' ? item.replace(/^"|"$/g, '').replace(/\\"/g, '"') : item
                          return typeof cleaned === 'string' ? JSON.parse(cleaned) : cleaned
                        } catch { return null }
                      }).filter(Boolean)
                    }
                  } catch {}
                  return { taskDescription: data.session.task || '', starterCode, testCases }
                })()}
                sessionId={deployment.sessionId}
                studentUserId={student.id}
                onBack={() => { setSelectedStudentId(null); setDetailTab('replay') }}
                onTabChange={setDetailTab}
                initialTab="replay"
                caseStudyData={caseStudyData}
              />
            </div>
          </div>
        )
      })()}
    </div>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: '0 32px 16px' },
  header: { display: 'flex', alignItems: 'center', padding: '20px 0 16px' },
  title: { fontSize: '20px', fontWeight: 700, color: '#111', margin: 0, letterSpacing: '-0.02em' },
  subtitle: { fontSize: '13px', color: '#999', margin: '3px 0 0' },
  searchRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e3', background: '#fff', flex: 1 },
  searchInput: { border: 'none', outline: 'none', fontSize: '14px', color: '#333', background: 'transparent', width: '100%' },
  sortSelect: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e3', background: '#fff' },
  sortDropdown: { border: 'none', outline: 'none', fontSize: '13px', color: '#444', background: 'transparent', cursor: 'pointer', fontWeight: 500 },
  pillRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  pillDivider: { width: '1px', height: '20px', backgroundColor: '#e0e0e3', margin: '0 4px' },
  filterCount: { fontSize: '12px', color: '#bbb', marginLeft: 'auto', fontWeight: 500 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: '16px', flex: 1, overflowY: 'auto', paddingBottom: '8px' },
  card: { display: 'flex', flexDirection: 'column', borderRadius: '12px', border: '1.5px solid #e8e8eb', background: '#fff', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s', maxWidth: '360px' },
  cardAvatarSmall: { width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' },
  cardName: { fontSize: '13px', fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardUsername: { fontSize: '11px', color: '#aaa', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardChips: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 500, color: '#666' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px 0 8px', flexShrink: 0 },
  pageBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '8px',
    border: '1.5px solid #e0e0e3', background: '#fff',
    fontSize: '13px', fontWeight: 500, color: '#444',
    transition: 'background 0.12s',
  },
  pageNumbers: { display: 'flex', alignItems: 'center', gap: '2px' },
  pageNum: {
    width: '32px', height: '32px', borderRadius: '8px',
    border: 'none', fontSize: '13px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.12s',
  },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  centeredState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' },
  spinner: { width: '32px', height: '32px', border: '3px solid #e8e8eb', borderTopColor: '#111', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  retryButton: { padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e0e0e3', background: '#fff', fontSize: '13px', fontWeight: 500, cursor: 'pointer' },
}